import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { consultationService } from '../../../services/consultationService';
import type { MedicalConsultation } from '../../../types/api';
import { useClinicSettings } from '../../../hooks/useClinicSettings';
import { parseApiDate } from '../../../lib/format';
import { usePrintGate } from '../hooks/usePrintGate';
import { delegatedSignatureLine, effectiveSignature } from '../hooks/delegatedSignature';
import DelegationLine from '../components/DelegationLine';
import { activePrescriptions } from '../hooks/correctionAccess';

const calcAge = (dob?: string) => {
  if (!dob) return null;
  return Math.floor((Date.now() - (parseApiDate(dob) ?? new Date(NaN)).getTime()) / (1000 * 60 * 60 * 24 * 365.25));
};

const fmt = (d?: string) =>
  d ? (parseApiDate(d) ?? new Date(NaN)).toLocaleDateString('es-DO', { day: '2-digit', month: 'long', year: 'numeric' }) : '-';

const ConsultationPrintPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [c, setC] = useState<MedicalConsultation | null>(null);
  const [error, setError] = useState('');
  const { clinicName, clinicAddress, clinicPhone, logoUrl } = useClinicSettings(c?.doctor_id);
  // The sign response carries only patient + doctor (no signer, no
  // prescriptions or other children), so printing it would drop the
  // delegation line's assistant name and the record's content. Reload the
  // full consultation first, as ConsultationDetailPage does.
  const reloadAfterSign = useCallback(() => {
    consultationService.getById(Number(id))
      .then(setC)
      .catch(() => setError('Error cargando la consulta.'));
  }, [id]);
  const { isBlocked, canSign, signing, signError, sign } = usePrintGate(c, reloadAfterSign);

  useEffect(() => {
    consultationService.getById(Number(id))
      .then(setC)
      .catch(() => setError('Error cargando la consulta.'));
  }, [id]);

  useEffect(() => {
    if (!c || isBlocked) return;
    const t = setTimeout(() => window.print(), 600);
    return () => clearTimeout(t);
  }, [c, isBlocked]);

  if (error) return <div style={{ padding: 40, color: 'red' }}>{error}</div>;
  if (!c) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'sans-serif' }}>
      <span>Cargando nota clínica...</span>
    </div>
  );

  // clinical-record-signing R8: this page renders client-side over the plain
  // GET endpoint and calls window.print() directly, bypassing generatePdf's
  // server-side gate entirely — must-fix item c.
  if (isBlocked) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'sans-serif', gap: 12 }}>
        <p>Esta nota clínica está pendiente de firma y no puede imprimirse.</p>
        {canSign ? (
          <button className="btn btn-primary" disabled={signing} onClick={() => sign()}>
            {signing ? 'Firmando...' : 'Firmar consulta'}
          </button>
        ) : (
          <p><em>Pendiente de firma.</em></p>
        )}
        {signError && <p style={{ color: 'red' }}>{signError}</p>}
      </div>
    );
  }

  const doctor = c.doctor;
  const patient = c.patient;
  const vs = c.vital_signs;
  const age = patient?.birth_date ? calcAge(patient.birth_date) : null;
  const pediatricData: any = c.specialty_data ? (c.specialty_data as any).pediatric : null;
  const traumatologiaData: any = c.specialty_data ? (c.specialty_data as any).traumatologia : null;
  const dermatologiaData: any = c.specialty_data ? (c.specialty_data as any).dermatologia : null;
  const oftalmologiaData: any = c.specialty_data ? (c.specialty_data as any).oftalmologia : null;
  const diagnosesPrincipal = (c.diagnoses || []).filter(d => (d.type || d.diagnosis_type) === 'principal');
  const diagnosesSecondary = (c.diagnoses || []).filter(d => (d.type || d.diagnosis_type) !== 'principal');

  return (
    <>
      <style>{`
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Times New Roman', Times, serif; font-size: 11pt; color: #111; background: white; }
        .page { width: 100%; max-width: 210mm; margin: 0 auto; padding: 16mm 18mm; min-height: 297mm; }
        .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #111; padding-bottom: 10px; margin-bottom: 12px; }
        .clinic-header-inner { display: flex; align-items: flex-start; gap: 12px; }
        .clinic-logo { max-width: 120px; height: auto; print-color-adjust: exact; }
        .clinic-name { font-size: 18pt; font-weight: bold; letter-spacing: 1px; }
        .doctor-info { text-align: right; font-size: 10pt; line-height: 1.5; }
        .doctor-name { font-size: 13pt; font-weight: bold; }
        .section { margin-bottom: 14px; }
        .section-title { font-size: 10pt; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; border-bottom: 1px solid #ccc; padding-bottom: 3px; margin-bottom: 8px; color: #333; }
        .row2 { display: flex; gap: 20px; }
        .col { flex: 1; }
        .label { font-size: 9pt; color: #555; }
        .value { font-size: 11pt; font-weight: 600; }
        .vitals-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; }
        .vital-box { border: 1px solid #ddd; border-radius: 4px; padding: 4px 8px; text-align: center; }
        .vital-val { font-size: 13pt; font-weight: bold; }
        .vital-label { font-size: 8pt; color: #666; }
        .diag-item { display: flex; gap: 8px; margin-bottom: 4px; align-items: flex-start; }
        .diag-badge { background: #1d4ed8; color: white; font-size: 8pt; padding: 1px 6px; border-radius: 3px; white-space: nowrap; font-weight: 600; }
        .diag-badge.secondary { background: #6b7280; }
        .rx-item { margin-bottom: 10px; }
        .rx-num { font-weight: bold; font-size: 12pt; margin-right: 6px; }
        .rx-med { font-size: 12pt; font-weight: bold; }
        .rx-sig { font-size: 10pt; color: #333; margin-left: 22px; font-style: italic; }
        .lab-item { display: inline-block; border: 1px solid #ddd; border-radius: 3px; padding: 2px 8px; margin: 2px; font-size: 10pt; }
        .text-block { font-size: 11pt; line-height: 1.7; white-space: pre-wrap; }
        .footer { margin-top: 30px; display: flex; justify-content: space-between; align-items: flex-end; }
        .signature-box { text-align: center; }
        .signature-line { border-top: 1px solid #111; width: 200px; margin: 0 auto 4px; }
        .followup-box { border: 1px solid #111; border-radius: 4px; padding: 8px 14px; font-size: 10pt; }
        .alert-alergias { background: #fee2e2; border: 1px solid #fca5a5; border-radius: 4px; padding: 6px 12px; font-size: 10pt; margin-bottom: 12px; }
        @media print {
          @page { margin: 12mm 15mm; size: A4; }
          body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          img { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="no-print" style={{ background: '#f0f0f0', padding: '10px 20px', display: 'flex', gap: 12, alignItems: 'center' }}>
        <button onClick={() => window.print()} style={{ padding: '6px 16px', background: '#1d4ed8', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>
          Imprimir / Guardar PDF
        </button>
        <button onClick={() => window.close()} style={{ padding: '6px 16px', background: '#6b7280', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
          Cerrar
        </button>
        <span style={{ fontSize: 12, color: '#555' }}>Ctrl+P para imprimir directamente</span>
      </div>

      <div className="page">
        {/* Header */}
        <div className="header">
          <div className="clinic-header-inner">
            {logoUrl && (
              <img
                src={logoUrl}
                alt="Logo de la clínica"
                className="clinic-logo"
              />
            )}
            <div className="clinic-info">
              <div className="clinic-name">{clinicName}</div>
              {clinicAddress && <div style={{ fontSize: '9pt', color: '#444', marginTop: 2 }}>{clinicAddress}</div>}
              {clinicPhone && <div style={{ fontSize: '9pt', color: '#444' }}>Tel: {clinicPhone}</div>}
            </div>
          </div>
          <div className="doctor-info">
            {doctor && (
              <>
                <div className="doctor-name">Dr. {doctor.first_name} {doctor.last_name}</div>
                {doctor.specialty && <div>{doctor.specialty.name}</div>}
                {(doctor as any).license_number && <div>Exequátur: {(doctor as any).license_number}</div>}
                {(doctor as any).phone && <div>Tel: {(doctor as any).phone}</div>}
              </>
            )}
          </div>
        </div>

        <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '12pt', marginBottom: 14, letterSpacing: 2 }}>
          NOTA CLÍNICA
        </div>

        {/* Patient + Date */}
        <div className="section">
          <div className="section-title">Paciente</div>
          <div className="row2">
            <div className="col">
              <div className="label">Nombre</div>
              <div className="value">{patient?.first_name} {patient?.last_name}</div>
            </div>
            <div className="col">
              <div className="label">Identificación</div>
              <div className="value">{patient?.identification || '-'}</div>
            </div>
            <div className="col">
              <div className="label">Edad</div>
              <div className="value">{age !== null ? `${age} años` : '-'}</div>
            </div>
            <div className="col">
              <div className="label">Fecha de Consulta</div>
              <div className="value">{fmt(c.consultation_date)}</div>
            </div>
          </div>
        </div>

        {/* Allergy warning */}
        {(patient as any)?.allergies?.length > 0 && (
          <div className="alert-alergias">
            ⚠ <strong>ALERGIAS:</strong> {(patient as any).allergies.map((a: any) => a.name).join(', ')}
          </div>
        )}

        {/* Chief Complaint */}
        <div className="section">
          <div className="section-title">Motivo de Consulta</div>
          <div className="text-block">{c.chief_complaint}</div>
        </div>

        {/* Present Illness */}
        {c.present_illness && (
          <div className="section">
            <div className="section-title">Enfermedad Actual</div>
            <div className="text-block">{c.present_illness}</div>
          </div>
        )}

        {/* Vital Signs */}
        {vs && (
          <div className="section">
            <div className="section-title">Signos Vitales</div>
            <div className="vitals-grid">
              {vs.systolic_pressure != null && (
                <div className="vital-box">
                  <div className="vital-val">{vs.systolic_pressure}/{vs.diastolic_pressure}</div>
                  <div className="vital-label">mmHg</div>
                </div>
              )}
              {vs.heart_rate != null && (
                <div className="vital-box">
                  <div className="vital-val">{vs.heart_rate}</div>
                  <div className="vital-label">FC/min</div>
                </div>
              )}
              {vs.respiratory_rate != null && (
                <div className="vital-box">
                  <div className="vital-val">{vs.respiratory_rate}</div>
                  <div className="vital-label">FR/min</div>
                </div>
              )}
              {vs.temperature != null && (
                <div className="vital-box">
                  <div className="vital-val">{vs.temperature}°</div>
                  <div className="vital-label">Temp °C</div>
                </div>
              )}
              {vs.oxygen_saturation != null && (
                <div className="vital-box">
                  <div className="vital-val">{vs.oxygen_saturation}%</div>
                  <div className="vital-label">SpO₂</div>
                </div>
              )}
              {vs.weight != null && (
                <div className="vital-box">
                  <div className="vital-val">{vs.weight}</div>
                  <div className="vital-label">kg</div>
                </div>
              )}
              {vs.height != null && (
                <div className="vital-box">
                  <div className="vital-val">{vs.height}</div>
                  <div className="vital-label">cm</div>
                </div>
              )}
              {vs.bmi != null && (
                <div className="vital-box">
                  <div className="vital-val">{vs.bmi}</div>
                  <div className="vital-label">IMC</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Physical Exam */}
        {c.physical_examination && (
          <div className="section">
            <div className="section-title">Examen Físico</div>
            <div className="text-block">{c.physical_examination}</div>
          </div>
        )}

        {/* Diagnoses */}
        {(c.diagnoses || []).length > 0 && (
          <div className="section">
            <div className="section-title">Diagnósticos</div>
            {diagnosisList(diagnosesPrincipal, 'principal')}
            {diagnosesSecondary.length > 0 && diagnosisList(diagnosesSecondary, 'secondary')}
          </div>
        )}

        {/* Prescriptions */}
        {activePrescriptions(c.prescriptions).length > 0 && (
          <div className="section">
            <div className="section-title">Tratamiento</div>
            {activePrescriptions(c.prescriptions).map((p, i) => (
              <div className="rx-item" key={p.id}>
                <span className="rx-num">Rp. {i + 1}</span>
                <span className="rx-med">{p.medication_name} {p.dosage}</span>
                <div className="rx-sig">
                  Sig: {p.frequency} — {p.duration}
                  {p.instructions && ` | ${p.instructions}`}
                </div>
                <DelegationLine text={delegatedSignatureLine(effectiveSignature(p, c), doctor)} />
              </div>
            ))}
          </div>
        )}

        {/* Lab Orders */}
        {(c.labOrders || []).length > 0 && (
          <div className="section">
            <div className="section-title">Órdenes de Laboratorio</div>
            <div>
              {c.labOrders!.map(l => (
                <span key={l.id} className="lab-item">
                  {l.panel?.name || l.test?.name || l.test_name}
                  {l.urgency === 'urgent' && <strong style={{ color: '#dc2626', marginLeft: 4 }}>URGENTE</strong>}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Imaging Orders */}
        {(c.imagingOrders || []).length > 0 && (
          <div className="section">
            <div className="section-title">Órdenes de Imágenes</div>
            <div>
              {c.imagingOrders!.map(img => (
                <span key={img.id} className="lab-item">
                  {img.imaging_study?.name || '-'}
                  {img.priority === 'urgent' && <strong style={{ color: '#dc2626', marginLeft: 4 }}>URGENTE</strong>}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Recommendations */}
        {c.general_recommendations && (
          <div className="section">
            <div className="section-title">Indicaciones Generales</div>
            <div className="text-block">{c.general_recommendations}</div>
          </div>
        )}

        {/* Pediatric Section */}
        {pediatricData && (
          <div className="section">
            <div className="section-title">Evaluación Pediátrica</div>

            {pediatricData.perinatal && (
              <div style={{ fontSize: '10pt', marginBottom: 6 }}>
                <strong>Antecedentes Perinatales:</strong>{' '}
                {[
                  pediatricData.perinatal.gestational_age_weeks != null ? `EG: ${pediatricData.perinatal.gestational_age_weeks} sem` : null,
                  pediatricData.perinatal.delivery_type ? `Parto: ${pediatricData.perinatal.delivery_type}` : null,
                  pediatricData.perinatal.birth_weight_g != null ? `Peso: ${pediatricData.perinatal.birth_weight_g}g` : null,
                  pediatricData.perinatal.birth_height_cm != null ? `Talla: ${pediatricData.perinatal.birth_height_cm}cm` : null,
                  pediatricData.perinatal.birth_head_cm != null ? `PC: ${pediatricData.perinatal.birth_head_cm}cm` : null,
                  pediatricData.perinatal.perinatal_complications ? `Comp: ${pediatricData.perinatal.perinatal_complications}` : null,
                ].filter(Boolean).join(', ')}
              </div>
            )}

            {pediatricData.feeding?.type && (
              <div style={{ fontSize: '10pt', marginBottom: 6 }}>
                <strong>Alimentación:</strong>{' '}
                {[
                  pediatricData.feeding.type === 'materna_exclusiva' ? 'Materna Exclusiva' :
                    pediatricData.feeding.type === 'materna_mixta' ? 'Materna Mixta' :
                    pediatricData.feeding.type === 'formula' ? 'Fórmula' :
                    pediatricData.feeding.type === 'ablactacion' ? 'Ablactación' :
                    pediatricData.feeding.type === 'complementaria' ? 'Complementaria' : pediatricData.feeding.type,
                  pediatricData.feeding.ablactation_start_month != null ? `Ablactación: ${pediatricData.feeding.ablactation_start_month}m` : null,
                  pediatricData.feeding.formula_name ? `Fórmula: ${pediatricData.feeding.formula_name}` : null,
                  pediatricData.feeding.food_allergies ? `Alergias: ${pediatricData.feeding.food_allergies}` : null,
                ].filter(Boolean).join(', ')}
              </div>
            )}

            {(pediatricData.growth?.weight_kg != null || pediatricData.growth?.height_cm != null || pediatricData.growth?.head_cm != null) && (
              <div style={{ fontSize: '10pt', marginBottom: 6 }}>
                <strong>Crecimiento:</strong>{' '}
                {[
                  pediatricData.growth?.weight_kg != null
                    ? `Peso: ${pediatricData.growth.weight_kg} kg${pediatricData.growth.weight_percentile != null ? ` (P${pediatricData.growth.weight_percentile})` : ''}`
                    : null,
                  pediatricData.growth?.height_cm != null
                    ? `Talla: ${pediatricData.growth.height_cm} cm${pediatricData.growth.height_percentile != null ? ` (P${pediatricData.growth.height_percentile})` : ''}`
                    : null,
                  pediatricData.growth?.head_cm != null
                    ? `PC: ${pediatricData.growth.head_cm} cm${pediatricData.growth.head_percentile != null ? ` (P${pediatricData.growth.head_percentile})` : ''}`
                    : null,
                  pediatricData.growth?.bmi != null
                    ? `IMC: ${pediatricData.growth.bmi.toFixed(1)}${pediatricData.growth.bmi_percentile != null ? ` (P${pediatricData.growth.bmi_percentile})` : ''}`
                    : null,
                ].filter(Boolean).join(', ')}
              </div>
            )}

            {pediatricData.milestones && pediatricData.milestones.length > 0 && (
              <div style={{ fontSize: '10pt', marginBottom: 6 }}>
                <strong>Hitos del Desarrollo:</strong>{' '}
                {pediatricData.milestones.filter((m: any) => m.achieved).length}/{pediatricData.milestones.length} hitos alcanzados
                {pediatricData.milestones_observations && ` — ${pediatricData.milestones_observations}`}
              </div>
            )}

            {pediatricData.vaccines && pediatricData.vaccines.length > 0 && (
              <div style={{ fontSize: '10pt', marginBottom: 6 }}>
                <strong>Vacunación (PAI):</strong>{' '}
                <span style={{
                  display: 'inline-block', padding: '1px 6px', borderRadius: 3, fontSize: '9pt',
                  fontWeight: 600, color: '#fff',
                  background: pediatricData.vaccination_status === 'completo' ? '#10b981'
                    : pediatricData.vaccination_status === 'incompleto' ? '#f59e0b' : '#6b7280',
                }}>
                  {pediatricData.vaccination_status === 'completo' ? 'Completo'
                    : pediatricData.vaccination_status === 'incompleto' ? 'Incompleto' : 'No iniciado'}
                </span>
                {' '}{pediatricData.vaccines.filter((v: any) => v.applied).length}/{pediatricData.vaccines.length} vacunas
                {pediatricData.vaccination_observations && ` — ${pediatricData.vaccination_observations}`}
              </div>
            )}
          </div>
        )}

        {/* Internal Medicine Section */}
        {(() => {
          const im: any = c.specialty_data ? (c.specialty_data as any).internal_medicine : null;
          if (!im) return null;
          const parts: string[] = [];

          if (im.diabetes?.type) {
            const d = im.diabetes;
            parts.push(`DM ${d.type}${d.hba1c != null ? ` · HbA1c ${d.hba1c}%` : ''}${d.fasting_glucose != null ? ` · GA ${d.fasting_glucose}` : ''}`);
          }

          if (im.hypertension?.stage) {
            const h = im.hypertension;
            parts.push(`HTA ${h.stage}${h.avg_sbp != null ? ` · ${h.avg_sbp}/${h.avg_dbp}` : ''}${h.target_organ_damage?.filter((o: any) => o.present).length > 0 ? ` · Daño órgano: ${h.target_organ_damage.filter((o: any) => o.present).map((o: any) => o.organ).join(', ')}` : ''}`);
          }

          if (im.dyslipidemia && (im.dyslipidemia.ldl != null || im.dyslipidemia.hdl != null)) {
            const dys = im.dyslipidemia;
            parts.push(`Dislipidemia: LDL ${dys.ldl ?? '-'} · HDL ${dys.hdl ?? '-'} · TG ${dys.triglycerides ?? '-'} · CT ${dys.total_cholesterol ?? '-'} mg/dL`);
          }

          if (im.cv_risk?.calculated_risk_percent != null) {
            const p = im.cv_risk.calculated_risk_percent;
            const cat = p < 10 ? 'Bajo' : p < 20 ? 'Moderado' : p < 30 ? 'Alto' : 'Muy Alto';
            parts.push(`RCV: ${p}% — ${cat}`);
          }

          if (im.metabolic_syndrome?.criteria_positive_count > 0) {
            parts.push(`SMet: ${im.metabolic_syndrome.criteria_positive_count}/5 — ${im.metabolic_syndrome.diagnosis ? 'POSITIVO' : 'NEGATIVO'}`);
          }

          if (im.treatment_plan && (im.treatment_plan.hba1c_target || im.treatment_plan.sbp_target != null || im.treatment_plan.next_appointment)) {
            const tp = im.treatment_plan;
            const targets = [tp.hba1c_target ? `HbA1c ${tp.hba1c_target}` : null, tp.sbp_target != null ? `PAS<${tp.sbp_target}` : null, tp.ldl_target ? `LDL ${tp.ldl_target}` : null].filter(Boolean).join(', ');
            const dates = [tp.next_appointment ? `Próx cita: ${(parseApiDate(tp.next_appointment) ?? new Date(NaN)).toLocaleDateString('es-DO')}` : null, tp.next_labs ? `Próx labs: ${(parseApiDate(tp.next_labs) ?? new Date(NaN)).toLocaleDateString('es-DO')}` : null].filter(Boolean).join(' · ');
            parts.push(`Plan: ${targets}${dates ? ` | ${dates}` : ''}`);
          }

          if (parts.length === 0) return null;

          return (
            <div className="section">
              <div className="section-title">Medicina Interna</div>
              {parts.map((line, i) => (
                <div key={i} style={{ fontSize: '10pt', lineHeight: 1.6 }}>{line}</div>
              ))}
            </div>
          );
        })()}

        {/* Traumatología Section */}
        {traumatologiaData && (
          <div className="section">
            <div className="section-title">Evaluación Traumatológica</div>
            <p className="section-content">
              {traumatologiaData.fracture?.type && `Fractura: ${traumatologiaData.fracture.type} en ${traumatologiaData.fracture.anatomic_location} (${traumatologiaData.fracture.side}). `}
              {traumatologiaData.fracture?.open_closed === 'abierta' && `Clasificación Gustilo: ${traumatologiaData.fracture.gustilo_classification}. `}
              {traumatologiaData.treatment?.type && `Tratamiento: ${traumatologiaData.treatment.type}. `}
              {traumatologiaData.treatment?.immobilization_type && `Inmovilización: ${traumatologiaData.treatment.immobilization_type}. `}
              {traumatologiaData.treatment?.surgical_plan && `Plan quirúrgico: ${traumatologiaData.treatment.surgical_plan}. `}
            </p>
          </div>
        )}

        {/* Oftalmología Section */}
        {oftalmologiaData && (
          <div className="section">
            <div className="section-title">Evaluación Oftalmológica</div>
            <p className="section-content">
              {oftalmologiaData.visual_acuity?.sc_od && `AV SC: OD ${oftalmologiaData.visual_acuity.sc_od} / OI ${oftalmologiaData.visual_acuity.sc_oi || ''}. `}
              {oftalmologiaData.visual_acuity?.cc_od && `AV CC: OD ${oftalmologiaData.visual_acuity.cc_od} / OI ${oftalmologiaData.visual_acuity.cc_oi || ''}. `}
              {oftalmologiaData.visual_acuity?.near_vision && `Visión cercana: ${oftalmologiaData.visual_acuity.near_vision}. `}
              {oftalmologiaData.refraction?.sphere_od && `Refracción OD: ${oftalmologiaData.refraction.sphere_od} ${oftalmologiaData.refraction.cylinder_od ? `(cil ${oftalmologiaData.refraction.cylinder_od}, eje ${oftalmologiaData.refraction.axis_od}°)` : ''}. `}
              {oftalmologiaData.refraction?.sphere_oi && `Refracción OI: ${oftalmologiaData.refraction.sphere_oi} ${oftalmologiaData.refraction.cylinder_oi ? `(cil ${oftalmologiaData.refraction.cylinder_oi}, eje ${oftalmologiaData.refraction.axis_oi}°)` : ''}. `}
              {oftalmologiaData.anterior_segment?.conjunctiva && `Segmento anterior: conjuntiva ${oftalmologiaData.anterior_segment.conjunctiva}, córnea ${oftalmologiaData.anterior_segment.cornea}, CA ${oftalmologiaData.anterior_segment.anterior_chamber}, cristalino ${oftalmologiaData.anterior_segment.lens}. `}
              {oftalmologiaData.posterior_segment?.vitreous && `Fondo de ojo: vítreo ${oftalmologiaData.posterior_segment.vitreous}, DD ${oftalmologiaData.posterior_segment.optic_disc}, mácula ${oftalmologiaData.posterior_segment.macula}, retina ${oftalmologiaData.posterior_segment.retina}. `}
              {oftalmologiaData.intraocular_pressure?.od != null && `PIO: OD ${oftalmologiaData.intraocular_pressure.od} / OI ${oftalmologiaData.intraocular_pressure.oi} mmHg (${oftalmologiaData.intraocular_pressure.method || ''}). `}
              {oftalmologiaData.diagnosis_oft?.primary && `Dx: ${oftalmologiaData.diagnosis_oft.primary} (${oftalmologiaData.diagnosis_oft.od_oi || ''})${oftalmologiaData.diagnosis_oft.icd10_code ? ` [${oftalmologiaData.diagnosis_oft.icd10_code}]` : ''}. `}
              {oftalmologiaData.treatment_oft?.eye_drops && `Tx: ${oftalmologiaData.treatment_oft.eye_drops}${oftalmologiaData.treatment_oft.eye_drops_frequency ? ` c/${oftalmologiaData.treatment_oft.eye_drops_frequency}` : ''}. `}
              {oftalmologiaData.treatment_oft?.oral_medication && `Oral: ${oftalmologiaData.treatment_oft.oral_medication}. `}
              {oftalmologiaData.treatment_oft?.surgery_indicated && `Cirugía: ${oftalmologiaData.treatment_oft.surgery_type || 'indicada'}. `}
              {oftalmologiaData.treatment_oft?.glasses_prescription && 'Prescripción de lentes. '}
              {oftalmologiaData.treatment_oft?.follow_up_days != null && `Seguimiento: ${oftalmologiaData.treatment_oft.follow_up_days} días. `}
            </p>
          </div>
        )}

        {/* Dermatología Section */}
        {dermatologiaData && (
          <div className="section">
            <div className="section-title">Evaluación Dermatológica</div>
            <p className="section-content">
              {dermatologiaData.lesion?.type && `Lesión: ${dermatologiaData.lesion.type}${dermatologiaData.lesion.size_mm != null ? ` (${dermatologiaData.lesion.size_mm}mm)` : ''} en ${dermatologiaData.lesion.anatomic_location || 'S/L'}. `}
              {dermatologiaData.lesion?.color && `Color: ${dermatologiaData.lesion.color}. `}
              {dermatologiaData.dermoscopy?.performed && dermatologiaData.dermoscopy.pattern && `Dermatoscopía: patrón ${dermatologiaData.dermoscopy.pattern}${dermatologiaData.dermoscopy.ABCD_rule_score != null ? ` (ABCD: ${dermatologiaData.dermoscopy.ABCD_rule_score})` : ''}. `}
              {dermatologiaData.dermoscopy?.melanoma_suspected && 'Sospecha de melanoma. '}
              {dermatologiaData.treatment_derma?.topical && `Tópico: ${dermatologiaData.treatment_derma.topical}. `}
              {dermatologiaData.treatment_derma?.systemic && `Sistémico: ${dermatologiaData.treatment_derma.systemic}. `}
              {(dermatologiaData.treatment_derma?.phototherapy || dermatologiaData.treatment_derma?.cryotherapy || dermatologiaData.treatment_derma?.laser || dermatologiaData.treatment_derma?.surgical_excision) && (
                `Procedimientos: ${[dermatologiaData.treatment_derma.phototherapy ? 'Fototerapia' : null, dermatologiaData.treatment_derma.cryotherapy ? 'Crioterapia' : null, dermatologiaData.treatment_derma.laser ? 'Láser' : null, dermatologiaData.treatment_derma.surgical_excision ? 'Escisión Qx' : null].filter(Boolean).join(', ')}. `
              )}
              {dermatologiaData.treatment_derma?.follow_up_weeks != null && `Seguimiento: ${dermatologiaData.treatment_derma.follow_up_weeks} semanas. `}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="footer">
          <div>
            {c.follow_up_date && (
              <div className="followup-box">
                <strong>Próxima cita:</strong> {fmt(c.follow_up_date)}
              </div>
            )}
          </div>
          <div className="signature-box">
            <div style={{ height: 50 }}></div>
            <div className="signature-line"></div>
            <div style={{ fontSize: '11pt', fontWeight: 'bold' }}>
              Dr. {doctor?.first_name} {doctor?.last_name}
            </div>
            {doctor?.specialty && <div style={{ fontSize: '10pt' }}>{doctor.specialty.name}</div>}
            {(doctor as any)?.license_number && (
              <div style={{ fontSize: '9pt', color: '#555' }}>Exequátur: {(doctor as any).license_number}</div>
            )}
            <DelegationLine text={delegatedSignatureLine(c, doctor)} />
          </div>
        </div>
      </div>
    </>
  );
};

function diagnosisList(items: MedicalConsultation['diagnoses'], type: 'principal' | 'secondary') {
  return (items || []).map(d => (
    <div className="diag-item" key={d.id}>
      <span className={`diag-badge ${type === 'secondary' ? 'secondary' : ''}`}>
        {d.diagnosis?.code || (type === 'principal' ? 'PRINCIPAL' : 'SECUNDARIO')}
      </span>
      <span>{d.diagnosis?.description || '-'}</span>
      {d.observations && <span style={{ color: '#555', fontSize: '10pt' }}> — {d.observations}</span>}
    </div>
  ));
}

export default ConsultationPrintPage;
